import React, { useState } from 'react';
import { ChevronDown, Target, Save, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useWorkbookGoal } from '@/hooks/goal-planner/use-goal-workbook-junction';
import { useUpdateGoalMutation } from '@/hooks/goal-planner/mutations/use-update-goal-mutation';
import { useToast } from '@/hooks/errors/use-toast';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface GoalSettingsContainerProps {
  pageId: string;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

const GoalSettingsContainer: React.FC<GoalSettingsContainerProps> = ({ 
  pageId,
  isOpen,
  onToggle 
}) => {
  const { toast } = useToast();
  const { data: goal, isLoading } = useWorkbookGoal(pageId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const updateGoalMutation = useUpdateGoalMutation();
  
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  // Sync with parent state if provided
  React.useEffect(() => {
    if (isOpen !== undefined) {
      setIsExpanded(isOpen);
    }
  }, [isOpen]);
  
  // Update form data when goal loads
  React.useEffect(() => {
    if (goal) {
      setFormData({
        title: goal.title,
        description: goal.description || ''
      });
    }
  }, [goal]);

  const handleInputChange = (field: string, value: string) => {
    setHasChanges(true);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast({
        title: 'Goal Title Required',
        description: 'Please enter a title for your goal',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateGoalMutation.mutateAsync({
        goalId: goal!.id,
        updates: {
          title: formData.title,
          description: formData.description
        }
      });
      
      toast({
        title: 'Goal Updated',
        description: 'Your goal has been updated successfully',
        variant: 'default',
      });
      
      setHasChanges(false);
      setIsExpanded(false); // Close accordion after save
    } catch (error) {
      console.error('Failed to update goal:', error);
      toast({
        title: 'Error',
        description: 'Failed to update goal',
        variant: 'destructive',
      });
    }
  };

  const isComplete = !!(goal && goal.title && goal.description);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="w-full p-4 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  // No goal state
  if (!goal) {
    return (
      <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-orange-600" />
          <p className="text-sm text-orange-800 dark:text-orange-200">
            No goal associated with this project
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
        {/* Accordion Header */}
        <button
          onClick={() => {
            const newState = !isExpanded;
            if (onToggle) {
              onToggle(newState);
            } else {
              setIsExpanded(newState);
            }
          }}
          className={cn(
            "w-full flex items-center justify-between p-4 rounded-lg transition-all",
            "bg-white dark:bg-gray-800",
            "border border-gray-200 dark:border-gray-700",
            "hover:border-purple-300 dark:hover:border-purple-600",
            isExpanded && "border-purple-300 dark:border-purple-600 shadow-sm"
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-lg transition-colors",
              isComplete 
                ? "bg-green-100 dark:bg-green-900/30" 
                : "bg-gray-100 dark:bg-gray-900/30"
            )}>
              <Target className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Goal Settings
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Define your project's main objective and success criteria
              </p>
            </div>
          </div>
          <ChevronDown 
            className={cn(
              "h-5 w-5 text-gray-400 transition-transform",
              isExpanded && "rotate-180"
            )} 
          />
        </button>

        {/* Accordion Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-6">
                {/* Edit Form (Always shown when expanded) */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="goal_title">
                      Goal Title <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="goal_title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Enter your goal title"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="goal_description">Goal Description</Label>
                    <Textarea
                      id="goal_description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe what you want to achieve..."
                      className="min-h-[120px]"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsExpanded(false);
                      setFormData({
                        title: goal.title,
                        description: goal.description || ''
                      });
                      setHasChanges(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={updateGoalMutation.isPending || !hasChanges}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                  >
                    {updateGoalMutation.isPending ? (
                      <>
                        <Save className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
    </div>
  );
};

export default GoalSettingsContainer;